/**
 * Spec 200 — outgoing work-sync helpers (Fase 5).
 *
 * Mobile's wire shape (mobile/src/sync/syncWorks.ts):
 *
 *   AES-CBC-256(teamKey, iv, JSON.stringify({
 *     type: 'upsert' | 'delete',
 *     work?:  Work,
 *     workId?: string,
 *     timestamp: number,
 *   }))
 *   → relay.send → WS { type: 'sync', companyId, data: ivHex + ':' + base64(ct) }
 *
 * The web has to produce the EXACT same bytes so a mobile peer
 * (running unchanged code) accepts the message and merges it via
 * its existing `processSync` last-write-wins logic. Conflict
 * resolution is identical for mobile↔mobile and mobile↔web —
 * latest `updatedAt` (or `serverTs`) wins, matching ARCH-C3.
 *
 * Why a separate file from `useLiveSync.ts`:
 *   `useLiveSync` is the INCOMING side (relay → IDB). This file is
 *   OUTGOING (mutation → relay). Splitting them keeps the test
 *   surfaces small — `applySyncBlob` is unit-tested headless;
 *   `broadcastWork` is unit-tested with a mocked relay.
 *
 * Threat model carry-over:
 *   The teamKey lives in IndexedDB (cleartext per spec 200 MVP §4.3).
 *   A future PIN-encrypted vault layer would inject decrypted bytes
 *   here; the wire format above doesn't change.
 */

import { aesCbcEncrypt, bytesToHex, hexToBytes, utf8ToBytes } from '../crypto/webCrypto';
import { saveWork as dbSaveWork, deleteWork as dbDeleteWork } from '../store/db';
import { relay } from './relay';
import type { WebIdentity } from '../store/keys';
import type { Work } from '../types';

/** Wire-shape for the encrypted blob the relay routes to peers. */
export interface SyncMessage {
  type: 'upsert' | 'delete';
  work?: Work;
  workId?: string;
  timestamp: number;
}

/**
 * Encrypt + send. Pure function over `(work, teamKeyHex)` so the
 * tests don't need a relay; in production the singleton is used.
 *
 * The IV is randomly generated per message — collision-resistance
 * comes from the 16-byte random IV space, not from a counter.
 */
async function encryptSyncBlob(message: SyncMessage, teamKeyHex: string): Promise<string> {
  const teamKey = hexToBytes(teamKeyHex);
  const iv = globalThis.crypto.getRandomValues(new Uint8Array(16));
  const plaintext = utf8ToBytes(JSON.stringify(message));
  const ct = await aesCbcEncrypt(teamKey, plaintext, iv);
  // Wire format: hex(iv) + ':' + base64(ciphertext). Mobile parses
  // by `String#indexOf(':')`, then base64 → bytes. Mirrors the
  // existing pre-SEC-21 mobile blob shape exactly.
  let ctBin = '';
  for (let i = 0; i < ct.length; i++) ctBin += String.fromCharCode(ct[i]);
  return `${bytesToHex(iv)}:${btoa(ctBin)}`;
}

/**
 * Persist + broadcast a Work upsert.
 *
 * Optimistic update: writes locally FIRST so the UI doesn't wait on
 * the WebSocket. The relay broadcast is fire-and-forget — if the
 * socket is down the relay client's queue will drain it on the next
 * reconnect (see `RelayClient._sendOrQueue`). Last-write-wins on the
 * other side handles ordering.
 *
 * Always sets `updatedAt = Date.now()` before persistence so the
 * conflict-resolution merge on peer devices picks the newer copy.
 */
export async function broadcastWork(work: Work, identity: WebIdentity): Promise<Work> {
  const stamped: Work = { ...work, updatedAt: Date.now() };
  await dbSaveWork(stamped);
  const blob = await encryptSyncBlob(
    { type: 'upsert', work: stamped, timestamp: Date.now() },
    identity.teamKey,
  );
  relay.send(blob);
  return stamped;
}

/**
 * Persist a delete locally and broadcast the tombstone.
 *
 * The relay routing is the same as upsert; peers receive the message
 * and call their own `deleteWork` path. Mobile's `processSync`
 * branches on `type === 'delete'` and calls `db.deleteWork(workId)`.
 */
export async function broadcastDelete(workId: string, identity: WebIdentity): Promise<void> {
  await dbDeleteWork(workId);
  const blob = await encryptSyncBlob(
    { type: 'delete', workId, timestamp: Date.now() },
    identity.teamKey,
  );
  relay.send(blob);
}

/** Test seam — exposed only so unit tests can verify the wire shape. */
export const __testing = { encryptSyncBlob };
